import Paragraph from '@tiptap/extension-paragraph';
import { DEFAULT_MARGIN_WIDTH } from './LineNumbering';

const TranscriptLine = Paragraph.extend({
  addAttributes() {
    return {
      lineType: {
        default: 'default',
        parseHTML: (element) => element.getAttribute('data-line-type') || 'default',
        renderHTML: (attributes) => {
          if (attributes.lineType === 'default') {
            return {};
          }
          return { 'data-line-type': attributes.lineType };
        },
      },
      marginContent: {
        default: '',
        parseHTML: (element) => {
          // Check new attribute first
          const newAttr = element.getAttribute('data-margin-content');
          if (newAttr) return newAttr;

          // Migration: old .ript files used data-has-margin="true"
          if (element.getAttribute('data-has-margin') === 'true') {
            const text = element.textContent || '';
            const slice = text.slice(0, 5);
            if (slice.length === 5 && /^(\d{1,3}\s{2,}|\s{5})$/.test(slice)) {
              return slice;
            }
            return '';
          }

          return '';
        },
        renderHTML: (attributes) => {
          if (!attributes.marginContent) {
            return {};
          }
          return { 'data-margin-content': attributes.marginContent };
        },
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-b': () => {
        this.editor.commands.toggleSpeechLine();
        return true;
      },
      'Alt-1': () => {
        this.editor.commands.toggleSpeechLine();
        return true;
      },
      'Alt-2': () => {
        // Embodied lines don't exist in audible mode — swallow the shortcut
        const mode = this.editor.storage.lineNumbers?.mode ?? 'multimodal';
        if (mode === 'audible') return true;
        this.editor.commands.toggleEmbodiedLine();
        return true;
      },
      'Alt-3': () => {
        this.editor.commands.setLineType('default');
        return true;
      },
      'Alt-4': () => {
        // Gloss lines only exist when multilingual is enabled — swallow otherwise
        if (!this.editor.storage.lineNumbers?.multilingual) return true;
        this.editor.commands.toggleGlossLine();
        return true;
      },
      'Alt-5': () => {
        // Translation lines only exist when multilingual is enabled — swallow otherwise
        if (!this.editor.storage.lineNumbers?.multilingual) return true;
        this.editor.commands.toggleTranslationLine();
        return true;
      },
      'Tab': () => {
        const marginWidth = this.editor.storage.lineNumbers?.marginWidth ?? DEFAULT_MARGIN_WIDTH;
        this.editor.commands.insertContent(' '.repeat(marginWidth));
        return true;
      },
      'Shift-Tab': () => {
        const marginWidth = this.editor.storage.lineNumbers?.marginWidth ?? DEFAULT_MARGIN_WIDTH;
        const { state } = this.editor;
        const { $from } = state.selection;
        const node = $from.parent;
        const text = node.textContent;

        // Count leading spaces (up to marginWidth)
        let spacesToRemove = 0;
        for (let i = 0; i < Math.min(text.length, marginWidth); i++) {
          if (text[i] === ' ') spacesToRemove++;
          else break;
        }

        if (spacesToRemove > 0) {
          const paragraphStart = $from.start();
          const tr = state.tr.delete(paragraphStart, paragraphStart + spacesToRemove);
          this.editor.view.dispatch(tr);
        }

        return true;
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const classes = [];
    if (node.attrs.lineType === 'speech') {
      classes.push('speech-line');
    } else if (node.attrs.lineType === 'embodied') {
      classes.push('embodied-line');
    } else if (node.attrs.lineType === 'gloss') {
      classes.push('gloss-line');
    } else if (node.attrs.lineType === 'translation') {
      classes.push('translation-line');
    }

    const attrs = { ...HTMLAttributes };
    if (classes.length > 0) {
      attrs.class = classes.join(' ');
    }

    return ['p', attrs, 0];
  },

  addCommands() {
    return {
      setLineType:
        (lineType) =>
        ({ commands }) => {
          return commands.updateAttributes('paragraph', { lineType });
        },

      toggleSpeechLine:
        () =>
        ({ editor, commands }) => {
          const { $from } = editor.state.selection;
          const node = $from.parent;
          const newType = node.attrs.lineType === 'speech' ? 'default' : 'speech';
          return commands.updateAttributes('paragraph', { lineType: newType });
        },

      toggleEmbodiedLine:
        () =>
        ({ editor, commands }) => {
          const { $from } = editor.state.selection;
          const node = $from.parent;
          const newType = node.attrs.lineType === 'embodied' ? 'default' : 'embodied';
          return commands.updateAttributes('paragraph', { lineType: newType });
        },

      toggleGlossLine:
        () =>
        ({ editor, commands }) => {
          const { $from } = editor.state.selection;
          const node = $from.parent;
          const newType = node.attrs.lineType === 'gloss' ? 'default' : 'gloss';
          return commands.updateAttributes('paragraph', { lineType: newType });
        },

      toggleTranslationLine:
        () =>
        ({ editor, commands }) => {
          const { $from } = editor.state.selection;
          const node = $from.parent;
          const newType = node.attrs.lineType === 'translation' ? 'default' : 'translation';
          return commands.updateAttributes('paragraph', { lineType: newType });
        },

      setAllLineTypes:
        (lineType) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph') {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  lineType,
                });
              }
            });
            dispatch(tr);
          }
          return true;
        },
    };
  },
});

export default TranscriptLine;
